import { useEffect, useRef, useState } from "react";
import InputField from "../../../reusable-components/TNInputField";
import {
  getColumns,
  componentConstants,
  formProjectDetailsContent,
  formProjectSummaryContent,
  subSection1_titles,
  subSection2_titles,
  DOCUMENT_GENERATION_STATUS,
} from "./constants";
import styles from "./LienDetailedView.module.scss";
import Form from "../../Form";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import { CiSearch } from "react-icons/ci";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import FileListDrawer from "./FileListDrawer";
import { useLocation } from "react-router-dom";
import LienProcessingService from "../../../services/lienProcess.service";
import { TriggerType } from "../../../enums/TriggerTypeEnums";
import { Storage } from "aws-amplify";
import { lienProcessConsts } from "../constants";
import RolesServices from "../../../services/roles.services";
import { storageKeys } from "../../../constants/storageKeys";
import { downloadUtil } from "../../../utils/downloadUtil";
import { Modal, message } from "antd";
import FileViewSection from "../../TicketProcessingView/DataInput/FileViewSection";
import clsx from "clsx";
import ConfirmGenerateModal from "./ConfirmGenerateModal";
import TNLoader from "../../../reusable-components/TNLoader";

const LienDetailedView = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const batchId = location?.pathname?.split("/")?.at(-1);
  const [searchValue, setSearchValue] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationIsModalOpen] =
    useState<boolean>(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { FetchLienDetailViewData, GenerateRecommendedDocuments } =
    LienProcessingService();
  const { data, refetch, isLoading, isFetching, isRefetching } =
    FetchLienDetailViewData({
      groupId: batchId,
      search: searchValue,
      page: currentPage,
      perPage: pageSize,
    });
  const [documentGenerationStatus, setDocumentGenerationStatus] =
    useState<string>("");
  const { mutateAsync: generateDocuments, isPending } =
    GenerateRecommendedDocuments();
  const {
    FIELD: { SEARCH_NAME, SEARCH_PLACEHOLDER, SEARCH_TYPE, SEARCH_ICON_ALT },
    BUTTON: { FORMS_BUTTON_LABEL },
    HEADERS: { PROJECT_DETAILS, PROJECT_SUMMARY },
    MODAL: { MODAL_WIDTH, DOWNLOAD_LABEL },
    DOCUMENTS_GENERATING_MESSAGE,
  } = componentConstants;

  const userDetails = localStorage.getItem(storageKeys.LS_USER_DETAILS);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[lienProcessConsts.ROLE_DETAILS_KEY];
  const userRoleProfileInfo =
    userDetailsJson?.[lienProcessConsts.PROFILE_DETAILS_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const editAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[3]?.selectedOptions?.includes(
        lienProcessConsts.EDIT_ROLE,
      )
    : true;

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const handleConfirmationModalOpen = () => setConfirmationIsModalOpen(true);
  const handleConfirmationModalClose = () => setConfirmationIsModalOpen(false);

  const handleGenerationResponse = (response: {
    status?: string;
    message?: string;
  }) => {
    if (response?.status) {
      setDocumentGenerationStatus(response.status);
    }

    if (response?.message) {
      message.info(response.message);
    }

    if (response?.status === DOCUMENT_GENERATION_STATUS.COMPLETED) {
      openDrawer();
    }
  };

  const initiateRecommendedFormsGeneration = async () => {
    try {
      const response = await generateDocuments(batchId);
      handleGenerationResponse(response);
    } catch (error) {
      message.error("Unable to check document generation status.");
    }
  };

  const handleGenerationConfirm = () => {
    handleConfirmationModalClose();
    initiateRecommendedFormsGeneration();
  };

  const handleRecommendedFormsClick = () => {
    const canStartGeneration = [
      DOCUMENT_GENERATION_STATUS.NOT_QUEUED,
      DOCUMENT_GENERATION_STATUS.FAILED,
    ].some((status) => status === documentGenerationStatus);

    if (canStartGeneration) {
      handleConfirmationModalOpen();
      return;
    }

    if (documentGenerationStatus === DOCUMENT_GENERATION_STATUS.COMPLETED) {
      openDrawer();
      return;
    }

    initiateRecommendedFormsGeneration();
  };

  useEffect(() => {
    setDocumentGenerationStatus(data?.data?.documentGenerationStatus);
  }, [data?.data]);

  const handleS3Links = (val?: string) => {
    if (true) {
      //TODO: logic check for rbac
      downloadUtil(`${process.env.REACT_APP_S3_BUCKET_URL}/${val}`);
    }
    return;
  };

  const [isPdfPreviewModalOpen, setPdfPreviewModalOpen] = useState(false);
  const handlePdfPreviewModalOpen = () => setPdfPreviewModalOpen(true);
  const handlePdfPreviewModalClose = () => setPdfPreviewModalOpen(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfName, setPdfName] = useState("");

  const handleSelectedRow = (record?: object) => {
    const url = `${process.env.REACT_APP_S3_BUCKET_URL}/${encodeURIComponent(
      record?.ticketImage,
    )}`;
    const imageName = record?.ticketImage?.split("/")?.at(-1);

    setPdfUrl(url);
    setPdfName(imageName);
    handlePdfPreviewModalOpen();
  };
  const columns = getColumns(handleS3Links, editAccess);
  const modalHeader = (
    <div className={styles.modalCustomHeader}>
      <div className={styles.modalLabel}>{pdfName}</div>
      <TNButton
        shape={ButtonShape.DEFAULT}
        size={ButtonSize.LARGE}
        type={ButtonType.PRIMARY}
        text={DOWNLOAD_LABEL}
        onClick={() => downloadUtil(pdfUrl)}
        disabled={!editAccess}
      />
    </div>
  );
  useEffect(() => {
    refetch();
  }, [batchId, searchValue, pageSize, currentPage]);
  return (
    <>
      <TNLoader isLoading={isFetching} />
      <Modal
        visible={isPdfPreviewModalOpen}
        onCancel={handlePdfPreviewModalClose}
        footer={<></>}
        width={MODAL_WIDTH}
        maskClosable={true}
        closeIcon={<></>}
        className={styles.pdfModal}
        title={modalHeader}
      >
        <div ref={wrapperRef} className={styles.mainFileView}>
          <FileViewSection url={pdfUrl} wrapperRef={wrapperRef} />
        </div>
      </Modal>
      <ConfirmGenerateModal
        isOpen={isConfirmationModalOpen}
        handleClose={handleConfirmationModalClose}
        handleConfirm={handleGenerationConfirm}
      />
      <Form>
        {({ getValues }) => (
          <>
            <FileListDrawer
              isOpen={isDrawerOpen}
              closeDrawer={closeDrawer}
              id={batchId}
            />
            <div className={styles.mainContainer}>
              <div className={styles.utilityContainer}>
                <div>
                  <InputField
                    type={SEARCH_TYPE}
                    name={SEARCH_NAME}
                    placeholder={SEARCH_PLACEHOLDER}
                    value={searchValue}
                    onKeyDown={(e) => {
                      if (e.key === TriggerType.ENTER) {
                        e.preventDefault();
                        setSearchValue(getValues(SEARCH_NAME));
                      }
                    }}
                    addonAfter={
                      <TNButton
                        shape={ButtonShape.DEFAULT}
                        size={ButtonSize.SMALL}
                        className={styles.searchFieldButton}
                        block
                        text={<CiSearch className={styles.searchFieldIcon} />}
                        onClick={() => {
                          setSearchValue(getValues(SEARCH_NAME));
                        }}
                      />
                    }
                  />
                </div>
                <div>
                  <TNButton
                    type={ButtonType.DEFAULT}
                    shape={ButtonShape.DEFAULT}
                    size={ButtonSize.LARGE}
                    text={FORMS_BUTTON_LABEL}
                    onClick={handleRecommendedFormsClick}
                    disabled={!editAccess || isPending}
                  />
                </div>
              </div>
              <div className={styles.dashboardContainer}>
                <div className={styles.projectDetailsSections}>
                  <div className={styles.cardHeader}>{PROJECT_DETAILS}</div>
                  {formProjectDetailsContent(data?.data?.projectDetails)}
                </div>
                <div className={styles.projectSummarySection}>
                  <div className={styles.projectSummarySubSection1}>
                    <div className={styles.cardHeader}>{PROJECT_SUMMARY}</div>
                    <div className={styles.SubSection}>
                      {formProjectSummaryContent(
                        subSection1_titles,
                        data?.data?.projectSummary,
                      )}
                    </div>
                  </div>
                  <div className={styles.projectSummarySubSection2}>
                    <div
                      className={clsx(
                        styles.SubSection,
                        styles.upcomingSubsection,
                      )}
                    >
                      {formProjectSummaryContent(
                        subSection2_titles,
                        data?.data?.projectSummary,
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.tableContainer}>
                <CustomTableWithPagination
                  columns={columns}
                  data={data?.data?.invoices ?? []}
                  className={styles.Table}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  handleSizeChangerChange={handlePaginationPageSize}
                  handleChange={handlePagination}
                  totalData={data?.data?.meta?.totalRecords}
                  loading={isLoading || isFetching || isRefetching}
                  isClickableTable
                  handleSelectedRow={handleSelectedRow}
                />
              </div>
            </div>
          </>
        )}
      </Form>
    </>
  );
};

export default LienDetailedView;
